/**
 * Slideout Panel Component
 * Handles slideout drawer panels with overlay and keyboard accessibility
 */

class SlideoutPanel {
  constructor() {
    this.panels = {};
    this.init();
  }

  init() {
    // Initialize all triggers
    document.querySelectorAll('[data-slideout-trigger]').forEach(trigger => {
      const panelId = trigger.getAttribute('data-slideout-trigger');
      const panel = document.getElementById(panelId);

      if (!panel) return;

      // Move panel to body to break out of any parent constraints
      // This ensures the panel can overlay the entire viewport
      if (panel.parentElement !== document.body) {
        document.body.appendChild(panel);
      }

      // Store panel reference
      this.panels[panelId] = {
        panel,
        trigger,
        overlay: panel.querySelector('[data-slideout-overlay]'),
        closeBtn: panel.querySelector('[data-slideout-close]')
      };

      // Bind events
      this.bindEvents(panelId);
    });
  }

  bindEvents(panelId) {
    const { trigger, overlay, closeBtn, panel } = this.panels[panelId];

    // Open on trigger click
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.open(panelId);
    });

    // Close on overlay click
    if (overlay) {
      overlay.addEventListener('click', () => {
        this.close(panelId);
      });
    }

    // Close on close button click
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.close(panelId);
      });
    }

    // Close on Escape key
    panel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close(panelId);
      }
    });
  }

  open(panelId) {
    const { panel, trigger } = this.panels[panelId];

    // Remove hidden attribute to make visible
    panel.hidden = false;

    // Force a reflow to ensure the transition works
    panel.offsetHeight;

    // Add open class to trigger animations
    requestAnimationFrame(() => {
      panel.classList.add('is-open');
    });

    // Update ARIA
    trigger.setAttribute('aria-expanded', 'true');

    // Prevent body scroll
    document.body.classList.add('slideout-panel-open');

    // Focus first focusable element in panel after animation starts
    setTimeout(() => {
      const closeBtn = panel.querySelector('[data-slideout-close]');
      if (closeBtn) {
        closeBtn.focus();
      }
    }, 100);
  }

  close(panelId) {
    const { panel, trigger } = this.panels[panelId];

    // Remove open class to trigger close animations
    panel.classList.remove('is-open');

    // Update ARIA
    trigger.setAttribute('aria-expanded', 'false');

    // Restore body scroll
    document.body.classList.remove('slideout-panel-open');

    // Wait for animation to complete before hiding
    setTimeout(() => {
      panel.hidden = true;
    }, 300); // Match the CSS transition duration

    // Return focus to trigger
    trigger.focus();
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.slideoutPanel = new SlideoutPanel();
  });
} else {
  window.slideoutPanel = new SlideoutPanel();
}

// Re-initialize when Shopify section is loaded (for theme editor)
document.addEventListener('shopify:section:load', () => {
  if (window.slideoutPanel) {
    window.slideoutPanel.init();
  }
});

export default SlideoutPanel;
